import EditableField from '@wandb/weave/common/components/EditableField';
import Editor from '@wandb/weave/common/components/Monaco/Editor';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Alert} from '@wandb/weave/components/Alert';
import {FancyPage} from '@wandb/weave/components/FancyPage';
import {IconName} from '@wandb/weave/components/Icon';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Button,
  Divider,
  Dropdown,
  Header,
  Image,
  Popup,
  Segment,
  Tab,
} from 'semantic-ui-react';

import YAML from '../../services/yaml';
import AgentTable from '../components/AgentTable';
import {Bash, Command} from '../components/Code';
import DeleteSweepModal from '../components/DeleteSweepModal';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../components/Launch';
import {Link as LaunchLink} from '../components/Launch/Link';
import {parseJobInput, quoteIfMultiWord} from '../components/Launch/utils';
import {Log} from '../components/Log';
import {MultiRunWorkspaceWithContext} from '../components/MultiRunWorkspace';
import {RunSelectorResizingContextProvider} from '../components/MultiRunWorkspace/RunSelectorResizingContext';
import NoMatch from '../components/NoMatch';
import RunSelector from '../components/RunSelector';
import {SWEEP_PRIOR_RUNS_DISABLED_FEATURES} from '../components/RunSelector/util';
import {RunState} from '../components/RunState';
import WBModal from '../components/WBModal';
import {
  LaunchAgent,
  RunQueueItem,
  useFetchJobVersionsQuery,
  useFetchLaunchAgentsFromProjectQuery,
  useFetchRunQueuesFromProjectQuery,
  useUpsertModelMutation,
  useUpsertSweepMutation,
} from '../generated/graphql';
import {Analytics} from '../services/analytics/Analytics';
import {
  SweepPageQueryData,
  useSweepPageQuery,
} from '../state/graphql/sweepPageQuery';
import {useDispatch} from '../state/hooks';
import * as PollingHooks from '../state/polling/hooks';
import {RunQueryContext} from '../state/runs/context';
import {ProjectPageViewSpec} from '../state/views/projectPage/types';
import DOC_URLS from '../util/doc_urls';
import {setDocumentDescription, setDocumentTitle} from '../util/document';
import {propagateErrorsContext} from '../util/errors';
import * as Filter from '../util/filters';
import {runNamesFilter} from '../util/filters';
import * as FilterTypes from '../util/filterTypes';
import {trackPageViewed} from '../util/navigation';
import {getDefaultPanelSectionConfig} from '../util/panelbankConfigs';
import {AccessOption} from '../util/permissions';
import * as RunTypes from '../util/runTypes';
import {RunSetConfig} from '../util/section';
import {toggleSelection} from '../util/selectionmanager';
import * as Sweeps from '../util/sweeps';
import {
  SweepState,
  sweepStateDisplayName,
  SweepYamlConfig,
} from '../util/sweeps';
import * as Url from '../util/urls';
import {JobPathParams} from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import NoAccessWrapper from './NoAccessPage/NoAccessWrapper';
import {ProjectPageContextProvider} from './ProjectPage/ProjectPageContext';
import * as S from './SweepPage.styles';

interface SweepPageProps {
  match: {
    params: {
      entityName: string;
      projectName: string;
      projectId: string;
      sweepName: string;
      tab?: string;
    };
  };
  history: any;
}

type UseSweepPageProps = ReturnType<typeof useSweepPageProps>;
type UseSweepPagePropsAfterLoad = Omit<UseSweepPageProps, 'sweepQuery'> & {
  project: NonNullable<SweepPageQueryData['project']>;
  refetch: Exclude<
    ReturnType<typeof useSweepPageQuery>,
    {
      loading: true;
    }
  >['refetch'];
};

type SweepPageAllProps = SweepPageProps & UseSweepPagePropsAfterLoad;

// TODO: There are a lot of unnecessary non-null assertions (Typescript's
//   ! operator). We can get rid of them by making fewer things optional in
//   ReportPanelGridSection
const SweepPage: FC<SweepPageAllProps> = React.memo(
  ({
    project,
    match,
    updateProject,
    configEditorValue,
    setConfigEditorValue,
    savingConfig,
    pollInterval,
    history,
    dispatch,
    upsertSweep,
    refetch,
    setSavingConfig,
  }) => {
    const sweep = project.sweep;
    const {entityName, projectName, sweepName, tab} = match.params;

    const isPublicOrOpenProject =
      project?.access === AccessOption.Public ||
      project?.access === AccessOption.Open;

    const config = YAML.parse(sweep.config) as SweepYamlConfig;

    const getSweepName = useCallback(() => {
      if (project.sweep) {
        return Sweeps.getSweepDisplayName(project.sweep);
      }
      return match.params.sweepName;
    }, [match, project]);

    const tabDescription: string = useMemo(() => {
      switch (tab) {
        case `overview`:
          return `Overview`;
        case `table`:
          return `Table`;
        case `agents`:
          return `Agents`;
        case `controls`:
          return `Controls`;
        case `logs`:
          return `Logs`;
        case `workspace`:
        default:
          return 'Workspace';
      }
    }, [tab]);

    useEffect(() => {
      setDocumentTitle(`${getSweepName()} | ${projectName} ${tabDescription}`);
      setDocumentDescription(
        `${tabDescription} of sweep ${getSweepName()} in ${projectName}, a machine learning project by ${entityName} using Weights & Biases.`
      );
    }, [tabDescription, getSweepName, projectName, entityName]);

    const sweepFilter: FilterTypes.Filter<RunTypes.Key> = {
      key: {section: 'run', name: 'sweepName'},
      op: '=',
      value: sweepName,
    };

    const baseUrl =
      '/' + entityName + '/' + projectName + '/sweeps/' + sweepName;

    const priorRunsEdges = sweep.priorRuns.edges;

    const priorRunNames = priorRunsEdges.map(edge => edge.node.name);
    const priorRunsFilter = runNamesFilter(priorRunNames);

    const updateDefaultSpec = (defaultSpec: ProjectPageViewSpec) => {
      const toggleSelectionInConfig = (
        runSetConfig: RunSetConfig,
        {
          node: run,
        }: {
          node: {
            name: string;
          };
        }
      ) =>
        toggleSelection(runSetConfig, run as RunTypes.Run, 1) as RunSetConfig;
      const oldConfig = defaultSpec.section.runSets![0];
      const newConfig = sweep.priorRuns.edges.reduce(
        toggleSelectionInConfig,
        oldConfig
      );
      return {
        ...defaultSpec,
        section: {
          ...defaultSpec.section,
          runSets: [newConfig],
          panelBankConfig: {
            ...defaultSpec.section.panelBankConfig,
            sections: [
              {
                ...getDefaultPanelSectionConfig({
                  name: 'Sweep',
                  type: 'grid',
                }),
                isOpen: true,
                panels: Sweeps.getDefaultPanels(project.sweep),
              },
              ...defaultSpec.section.panelBankConfig.sections,
            ],
          },
        },
      };
    };

    const onSetTableExpanded = (isTableExpanded: boolean) => {
      history.push({
        pathname: baseUrl + (isTableExpanded ? '/table' : '/workspace'),
        search: window.location.search,
      });
    };

    const overviewTabBody = () => (
      <SweepOverview
        configEditorValue={configEditorValue}
        getSweepName={getSweepName}
        history={history}
        match={match}
        project={project}
        refetch={refetch}
        savingConfig={savingConfig}
        setConfigEditorValue={setConfigEditorValue}
        setSavingConfig={setSavingConfig}
        updateProject={updateProject}
        upsertSweep={upsertSweep}
      />
    );

    const workspaceTabBody = (isTableExpanded: boolean) => () =>
      (
        <MultiRunWorkspaceWithContext
          defaultSpecPostFn={updateDefaultSpec}
          entityName={entityName}
          isTableExpanded={isTableExpanded}
          mergeFilters={Filter.Or([sweepFilter, priorRunsFilter])}
          onSetTableExpanded={onSetTableExpanded}
          projectName={projectName}
          recommendGrouping="group-and-jobtype"
          runSetName={`Sweep: ${getSweepName()}`}
          runsQueryContextValue={{
            sweep: {
              id: sweep.id,
              earlyTerminate: config.early_terminate != null,
            },
          }}
          title={`Sweep: ${getSweepName()}`}
          viewType="sweep-view"
          workspaceObjectID={sweep.name}
        />
      );

    const agentTabBody = () => <SweepAgents project={project} />;

    const sweepControlsTabBody = () => (
      <SweepControls
        project={project}
        upsertSweep={upsertSweep}
        refetch={refetch}
      />
    );

    const logsTabBody = () => (
      <SweepLogs project={project} match={match} pollInterval={pollInterval} />
    );

    return (
      <div style={{marginLeft: 0, marginRight: 0}}>
        <NoAccessWrapper skipCheck={isPublicOrOpenProject}>
          <ProjectPageContextProvider
            project={{
              id: project.id,
              name: projectName,
              entityName: entityName,
            }}>
            <FancyPage
              baseUrl={baseUrl}
              activeSlug={tab ?? 'workspace'}
              items={[
                {
                  iconName: 'info',
                  name: 'Overview',
                  slug: 'overview',
                  render: overviewTabBody,
                },
                {
                  iconName: 'dashboard-blackboard',
                  name: 'Workspace',
                  slug: 'workspace',
                  render: workspaceTabBody(false),
                },
                {
                  iconName: 'table',
                  name: 'Runs',
                  slug: 'table',
                  render: workspaceTabBody(true),
                },
                {
                  iconName: 'ghost-agent',
                  name: 'Agents',
                  slug: 'agents',
                  render: agentTabBody,
                },
                {
                  iconName: 'settings-parameters',
                  name: 'Controls',
                  slug: 'controls',
                  render: sweepControlsTabBody,
                },
                ...(sweep.controllerRunName
                  ? [
                      {
                        iconName: 'list' as IconName,
                        name: 'Logs',
                        slug: 'logs',
                        render: logsTabBody,
                      },
                    ]
                  : []),
              ]}
            />
          </ProjectPageContextProvider>
        </NoAccessWrapper>
      </div>
    );
  }
);

function useSweepPageProps(
  entityName: string,
  projectName: string,
  sweepName: string
) {
  const [configEditorValue, setConfigEditorValue] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  const sweepQuery = useSweepPageQuery(
    {
      entityName,
      projectName,
      sweepName,
    },
    {
      fetchPolicy: 'network-only',
      enablePolling: true,
    }
  );

  const pollInterval = PollingHooks.usePollInterval();

  const sweepConfig =
    (!sweepQuery.loading && sweepQuery.data?.project?.sweep?.config) || '';
  useEffect(() => {
    setConfigEditorValue(sweepConfig);
  }, [sweepConfig]);

  const [updateProject] = useUpsertModelMutation();
  const [upsertSweep] = useUpsertSweepMutation({
    // Show the error message to the user instead of rendering error page
    context: propagateErrorsContext(),
    onError: ({networkError}) => {
      const message = (networkError as any)?.result?.errors[0]?.message;
      if (message != null) {
        alert(message);
      }
    },
  });

  const dispatch = useDispatch();

  return {
    dispatch,
    updateProject,
    sweepQuery,
    upsertSweep,
    configEditorValue,
    setConfigEditorValue,
    savingConfig,
    setSavingConfig,
    pollInterval,
  };
}

export default (props: SweepPageProps) => {
  const {entityName, projectName, sweepName} = props.match.params;
  const selectedProps = useSweepPageProps(entityName, projectName, sweepName);

  if (selectedProps.sweepQuery.loading) {
    return <Loader name="sweep-query" />;
  }
  if (
    selectedProps.sweepQuery.data?.project == null ||
    selectedProps.sweepQuery.data?.project.sweep == null
  ) {
    return <NoMatch />;
  }

  return (
    <SweepPage
      {...props}
      {...selectedProps}
      project={selectedProps.sweepQuery.data?.project}
      refetch={selectedProps.sweepQuery.refetch}
    />
  );
};

type SweepOverviewProps = Pick<
  SweepPageAllProps,
  | 'match'
  | 'project'
  | 'history'
  | 'upsertSweep'
  | 'updateProject'
  | 'configEditorValue'
  | 'setConfigEditorValue'
  | 'savingConfig'
  | 'setSavingConfig'
  | 'refetch'
> & {
  getSweepName: () => string;
};

const SweepOverview: React.FC<SweepOverviewProps> = React.memo(
  ({
    match,
    project,
    history,
    getSweepName,
    upsertSweep,
    updateProject,
    configEditorValue,
    setConfigEditorValue,
    savingConfig,
    setSavingConfig,
    refetch,
  }) => {
    // for page view tracking
    useEffect(() => {
      trackPageViewed('Sweep', 'Overview');
    }, []);

    const sweep = project.sweep;
    const {entityName, projectName, sweepName} = match.params;
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);

    // Launch info setup
    const launchScheduler: Record<string, string> | null = JSON.parse(
      sweep.scheduler
    );
    const rawQueueStr: string | undefined = launchScheduler?.queue;
    const rawQueueEntityStr: string | undefined =
      launchScheduler?.run_queue_entity;
    const [launchSchedulerQueueID, setLaunchSchedulerQueueID] = useState('');
    const runQueuesQuery = useFetchRunQueuesFromProjectQuery({
      variables: {
        projectName: DEFAULT_LAUNCH_PROJECT_NAME,
        entityName: rawQueueEntityStr ?? entityName,
      },
      skip: !rawQueueStr,
    });

    const runQueues =
      runQueuesQuery.data?.project?.runQueues.filter(
        x => x.name.localeCompare(rawQueueStr ?? '') === 0
      ) ?? [];
    const runQueue = runQueues.length > 0 ? runQueues[0] : null;

    const schedulerRQI: RunQueueItem | null = useMemo(() => {
      if (!runQueue?.runQueueItems) {
        return null;
      }

      setLaunchSchedulerQueueID(runQueue?.id);

      let rqis = runQueue?.runQueueItems.edges.map(
        (rqi: {node: Partial<RunQueueItem>}) => rqi.node
      ) as RunQueueItem[];
      rqis = rqis.filter(
        rqi => rqi.id.localeCompare(launchScheduler?.scheduler_id ?? '') === 0
      );

      if (rqis.length > 0) {
        return rqis[0];
      }
      return null;
    }, [runQueue, launchScheduler?.scheduler_id]);

    const jobFullName = getJobNameFromRunSpec(schedulerRQI?.runSpec);
    const jobPathParams = useJobPathParams(entityName, jobFullName);
    const runningAgents = useAgentQuery(projectName, entityName, runQueue?.id);

    const priorRunsEdges = sweep.priorRuns.edges;
    const priorRunNames = priorRunsEdges.map(edge => edge.node.name);
    const priorRunsFilter = runNamesFilter(priorRunNames);
    const priorRunsTable = () => (
      <RunQueryContext.Provider
        value={{
          entityName,
          projectName,
          mergeFilters: priorRunsFilter,
        }}>
        <RunSelectorResizingContextProvider>
          <RunSelector
            className="single-wb-table"
            pageEntityName={entityName}
            pageProjectName={projectName}
            disabledFeatures={SWEEP_PRIOR_RUNS_DISABLED_FEATURES}
            pollInterval={0}
          />
        </RunSelectorResizingContextProvider>
      </RunQueryContext.Provider>
    );

    const canReconfigure = useCallback(() => {
      return project.sweep.state === 'PENDING';
    }, [project]);

    const saveConfig = useCallback(async () => {
      if (!canReconfigure() || savingConfig) {
        return;
      }

      Analytics.track('saved edited sweep config in UI', {
        entityName: project.entity.name,
        sweepId: sweep.id,
      });

      if (sweep != null) {
        setSavingConfig(true);
        await upsertSweep({
          variables: {id: sweep.id, config: configEditorValue},
        });
        await refetch();
        setSavingConfig(false);
      }
    }, [
      canReconfigure,
      savingConfig,
      sweep,
      refetch,
      upsertSweep,
      configEditorValue,
      setSavingConfig,
      project.entity.name,
    ]);

    const resetConfig = useCallback(() => {
      if (savingConfig) {
        return;
      }
      setConfigEditorValue(project.sweep.config);
    }, [project, savingConfig, setConfigEditorValue]);

    const canSaveConfig = useCallback(() => {
      if (!canReconfigure() || project == null) {
        return false;
      }
      return project.sweep.config !== configEditorValue;
    }, [canReconfigure, configEditorValue, project]);

    let configDownloadURL = '';
    if (sweep.config != null && sweep.config.length > 0) {
      const file = new Blob([sweep.config], {
        type: 'text/yaml',
      });
      configDownloadURL = URL.createObjectURL(file);
    }

    return (
      <div className="sweep-overview">
        <DeleteSweepModal
          open={deleteConfirmModalOpen}
          sweeps={[sweep]}
          onClose={() => setDeleteConfirmModalOpen(false)}
          onDelete={() =>
            history.push(
              Url.project({
                name: projectName,
                entityName,
              })
            )
          }
        />
        <div className="sweep-overview-top">
          <div className="mini-page-header">
            <div className="name-header-wrapper">
              <EditableField
                className="name-header"
                readOnly={project.readOnly}
                asHeader="h1"
                placeholder={getSweepName()}
                showEditIcon
                value={getSweepName()}
                save={displayName => {
                  upsertSweep({
                    variables: {id: sweep.id, displayName},
                  });
                }}
              />
              <Dropdown
                className="dropdown-menu-icon-button"
                floating
                trigger={<Button size="tiny" icon="ellipsis vertical" />}
                direction="left">
                <Dropdown.Menu>
                  <Dropdown.Item
                    icon="trash"
                    content="Delete sweep"
                    disabled={project.readOnly}
                    onClick={() => setDeleteConfirmModalOpen(true)}
                  />
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <EditableField
              className="description"
              readOnly={project.readOnly}
              multiline
              showEditIcon
              renderLinks
              placeholder={'What makes this sweep special?'}
              value={sweep.description}
              save={value => {
                upsertSweep({variables: {id: sweep.id, description: value}});
              }}
            />
          </div>
          <div className="overview-item">
            <div className="overview-key">ID</div>
            <div className="overview-value">{sweep.name}</div>
          </div>
          {launchScheduler && (
            <div className="overview-item">
              <div className="overview-key">Status</div>
              <div className="overview-value">
                <RunState
                  value={sweepStateDisplayName(
                    sweep.state as SweepState
                  ).toLowerCase()}
                />
              </div>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">Created</div>
            <div className="overview-value">
              {new Date(sweep.createdAt).toLocaleString()}
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-key">Last updated</div>
            <div className="overview-value">
              {new Date(sweep.updatedAt).toLocaleString()}
            </div>
          </div>
          {launchScheduler && jobFullName && (
            <div className="overview-item">
              <div className="overview-key">Job</div>
              <div className="overview-item">
                {jobPathParams ? (
                  <div className="overview-value">
                    <LaunchLink to={Url.job(jobPathParams)}>
                      {jobFullName}
                    </LaunchLink>
                  </div>
                ) : (
                  <div className="overview-value">{jobFullName}</div>
                )}
              </div>
            </div>
          )}
          {!launchScheduler ? (
            <div className="overview-item">
              <div className="overview-key">Launch agent</div>
              <div className="overview-value">
                <Bash>
                  <Command>
                    wandb agent {entityName}/{quoteIfMultiWord(projectName)}/
                    {sweepName}
                  </Command>
                </Bash>
              </div>
            </div>
          ) : (
            <>
              <div className="overview-item">
                <div className="overview-key">Launch queue</div>
                {launchSchedulerQueueID !== '' ? (
                  <div className="overview-value">
                    <LaunchLink
                      to={Url.queue(
                        rawQueueEntityStr ?? entityName,
                        launchSchedulerQueueID
                      )}>
                      {rawQueueStr}
                    </LaunchLink>
                  </div>
                ) : (
                  // Scheduler run queue item older than 7 days, not in queue
                  <div className="overview-value">{rawQueueStr}</div>
                )}
                {launchScheduler &&
                  runningAgents &&
                  runningAgents.length === 0 && (
                    <S.Value2>
                      <Alert severity="error">No running launch agents</Alert>
                    </S.Value2>
                  )}
              </div>
            </>
          )}
          <div className="overview-item">
            <div className="overview-key">Author</div>
            <div className="overview-value">
              <Link className="user-link" to={`/${sweep.user?.username}`}>
                <Image
                  src={sweep.user?.photoUrl}
                  avatar
                  onError={(i: any) => (i.target.style.display = 'none')}
                />
                {sweep.user?.username}
              </Link>
            </div>
          </div>
          {sweep.state === 'FLAPPING' && (
            <S.ErrorWrapper>
              <S.ErrorMessage>
                The sweep was stopped automatically due to a high number of
                crashed runs. Please update your sweep with a working
                configuration, then resume from the Sweep Controls page.
              </S.ErrorMessage>
            </S.ErrorWrapper>
          )}
        </div>
        <S.SweepConfigBody>
          {priorRunNames.length > 0 && (
            <>
              <Header as="h3">Prior Runs ({priorRunNames.length})</Header>
              {priorRunsTable()}
            </>
          )}
          {Boolean(sweep.config) && (
            <>
              <Header as="h3">Sweep configuration</Header>
              {canReconfigure() && (
                <>
                  <p>
                    To change your sweep configuration,{' '}
                    <a href={configDownloadURL} download="sweep.yaml">
                      download your configuration file
                    </a>{' '}
                    and save it as sweep.yaml.
                  </p>
                  <p>
                    Modify it as you like then run the command below to upload
                    the new configuration.
                  </p>
                  <Bash>
                    <Command>
                      wandb sweep --update {entityName}/{projectName}/
                      {sweep.name} sweep.yaml
                    </Command>
                  </Bash>
                  <p>
                    Check out our{' '}
                    <TargetBlank href={DOC_URLS.sweeps} className="doc-link">
                      docs
                    </TargetBlank>{' '}
                    for more information.
                  </p>
                  <p>
                    You can also edit your configuration in the editor below:
                  </p>
                </>
              )}
              <div className="sweep-config-wrapper">
                {canReconfigure() ? (
                  <Editor
                    value={configEditorValue}
                    onChange={setConfigEditorValue}
                    language="yaml"
                  />
                ) : (
                  <pre>{sweep.config}</pre>
                )}
              </div>
              {canReconfigure() && (
                <div className="sweep-config-save">
                  <Button
                    size="tiny"
                    disabled={!canSaveConfig() || savingConfig}
                    onClick={resetConfig}>
                    Cancel
                  </Button>
                  <Button
                    size="tiny"
                    primary
                    disabled={!canSaveConfig() || savingConfig}
                    loading={savingConfig}
                    onClick={saveConfig}>
                    Save
                  </Button>
                </div>
              )}
            </>
          )}
        </S.SweepConfigBody>
      </div>
    );
  }
);

type SweepAgentsProps = Pick<SweepPageAllProps, 'project'>;

const SweepAgents = React.memo(({project}: SweepAgentsProps) => {
  // for page view tracking
  useEffect(() => {
    trackPageViewed('Sweep', 'Agents');
  }, []);

  return (
    <Tab.Pane>
      <div style={{marginBottom: '40px'}}>
        <AgentTable agents={project.sweep.agents.edges.map(e => e.node)} />
      </div>
    </Tab.Pane>
  );
});
SweepAgents.displayName = 'SweepAgents';

type SweepControlsProps = Pick<
  SweepPageAllProps,
  'project' | 'upsertSweep' | 'refetch'
>;

const SweepControls: React.FC<SweepControlsProps> = React.memo(
  ({project, upsertSweep, refetch}) => {
    // for page view tracking
    useEffect(() => {
      trackPageViewed('Sweep', 'Controls');
    }, []);

    const sweep = project.sweep;
    const [killConfirmModalOpen, setKillConfirmModalOpen] = useState(false);

    const setSweepState = useCallback(
      (st: string) => {
        // These will either both be set, or both be unset. This is just for typing.
        if (sweep != null) {
          upsertSweep({variables: {id: sweep.id, state: st}}).then(() =>
            refetch()
          );
        }
      },
      [sweep, refetch, upsertSweep]
    );

    const launchScheduler: Record<string, string> | null = JSON.parse(
      sweep.scheduler
    );

    const state: SweepState = (sweep.state as SweepState) || 'UNKNOWN';
    const pauseMsg = 'Pause the sweep to temporarily stop running new runs.';
    const pauseLaunchMsg =
      'Pause the sweep scheduler to stop enqueuing new runs.';
    const resumeMsg = 'Resume the sweep to continue running new runs.';
    const finishMsg =
      'Finish the sweep to stop running new runs and let currently running runs finish.';
    const restartMsg =
      'Resume running the sweep. If you exhausted a grid search, no new runs will run unless you delete some of the previous runs.';
    const cancelMsg =
      'Cancel the sweep to kill all running runs and stop running new runs.';
    const flappingMsg =
      'This sweep was automatically stopped due to a high number of crashed runs. Please update your sweep with a working configuration before resuming.';
    return (
      <Tab.Pane>
        <WBModal
          open={killConfirmModalOpen}
          onClose={() => setKillConfirmModalOpen(false)}
          includeCancel
          primaryAction={{
            content: 'Kill',
            onClick: () => {
              setSweepState('CANCELED');
              setKillConfirmModalOpen(false);
            },
          }}
          header="Are you sure you want to kill this sweep?"
          headerIcon="warning">
          <p>
            This will kill all runs in this sweep and stop running new runs.
          </p>
        </WBModal>
        <Segment padded="very">
          <Header as="h3">
            Sweep state: {'  '}
            <RunState
              value={sweepStateDisplayName(
                sweep.state as SweepState
              ).toLowerCase()}
            />
          </Header>
          {launchScheduler ? (
            <>
              {state === 'PENDING' ? (
                <p style={{marginBottom: 32}}>
                  <span className="hint-text">
                    Create a launch agent on queue:{' '}
                    <Link title="Launch run queue" to={Url.launchOverview()}>
                      {launchScheduler.queue}
                    </Link>{' '}
                    to start your sweep. You can't reconfigure the sweep once it
                    has started.
                  </span>
                </p>
              ) : state === 'RUNNING' ? (
                <p style={{marginBottom: 32}}>
                  <span className="hint-text">
                    This sweep is running on launch queue:{' '}
                    <Link title="Launch run queue" to={Url.launchOverview()}>
                      {launchScheduler.queue}
                    </Link>{' '}
                  </span>
                </p>
              ) : null}
            </>
          ) : (
            <p style={{marginBottom: 32}}>
              <span className="hint-text">
                Create an agent to start your sweep. You can't reconfigure the
                sweep once it has started.
              </span>
            </p>
          )}
          <Divider style={{marginTop: 48, marginBottom: 48}} />
          <Header as="h4">Pause / Resume</Header>
          <p style={{marginBottom: 32}}>
            <span className="hint-text">
              {state === 'PAUSED' && resumeMsg}
              {state === 'RUNNING' && launchScheduler
                ? pauseLaunchMsg
                : pauseMsg}
            </span>
          </p>
          <p>
            <Popup
              trigger={
                <Button
                  size="small"
                  icon="play"
                  content="Unpause"
                  disabled={state !== 'PAUSED'}
                  onClick={() => setSweepState('RUNNING')}
                />
              }
              content={resumeMsg}
            />
            {state === 'RUNNING' && (
              <Popup
                trigger={
                  <Button
                    size="small"
                    icon="pause"
                    content="Pause"
                    onClick={() => setSweepState('PAUSED')}
                  />
                }
                content={launchScheduler ? pauseLaunchMsg : pauseMsg}
              />
            )}
          </p>
          <Divider style={{marginTop: 48, marginBottom: 48}} />
          <Header as="h4">Stop</Header>
          <p style={{marginBottom: 32}}>
            <span className="hint-text">{finishMsg}</span>
          </p>
          <p>
            <Popup
              trigger={
                <Button
                  color="red"
                  size="small"
                  icon="check"
                  content="Stop"
                  disabled={state !== 'RUNNING' && state !== 'PAUSED'}
                  onClick={() => setSweepState('FINISHED')}
                />
              }
              content={finishMsg}
            />
            {state === 'FINISHED' && !launchScheduler && (
              <Popup
                trigger={
                  <Button
                    size="small"
                    icon="play"
                    content="Resume"
                    onClick={() => setSweepState('RUNNING')}
                  />
                }
                content={restartMsg}
              />
            )}
            {state === 'FLAPPING' && (
              <Popup
                trigger={
                  <Button
                    size="small"
                    icon="play"
                    content="Resume"
                    onClick={() => setSweepState('RUNNING')}
                  />
                }
                content={flappingMsg}
              />
            )}
          </p>
          <Divider style={{marginTop: 48, marginBottom: 48}} />
          <Header as="h4">Cancel</Header>
          <p style={{marginBottom: 32}}>
            <span className="hint-text">{cancelMsg}</span>
          </p>
          <p>
            <Popup
              trigger={
                <Button
                  color="red"
                  size="small"
                  icon="cancel"
                  disabled={!['RUNNING', 'PAUSED', 'PENDING'].includes(state)}
                  content="Kill"
                  onClick={() => setKillConfirmModalOpen(true)}
                />
              }
              content={cancelMsg}
            />
          </p>
        </Segment>
      </Tab.Pane>
    );
  }
);

type SweepLogsProps = Pick<
  SweepPageAllProps,
  'project' | 'match' | 'pollInterval'
>;

const SweepLogs: React.FC<SweepLogsProps> = React.memo(
  ({project, match, pollInterval}) => {
    // for page view tracking
    useEffect(() => {
      trackPageViewed('Sweep', 'Logs');
    }, []);

    const {entityName, projectName} = match.params;
    const sweep = project.sweep;

    if (!sweep.controllerRunName) {
      return null;
    }
    // const outputLogFile = RunHelpers.outputLogFile(run);
    return (
      <Log
        entityName={entityName}
        projectName={projectName}
        runName={sweep.controllerRunName}
        pollInterval={pollInterval}
        outputLogFile={null}
        showTimestamp
        analyticsLocation="sweep-logs"
      />
    );
  }
);

const useAgentQuery = (
  projectName: string,
  entityName: string,
  runQueueID: string | undefined
): LaunchAgent[] | null => {
  // Hook that returns the running agents on a given queue
  const launchAgentsQuery = useFetchLaunchAgentsFromProjectQuery({
    variables: {
      projectName,
      entityName,
    },
    skip: !runQueueID,
  });
  const launchAgentEntityQuery = useFetchLaunchAgentsFromProjectQuery({
    variables: {
      projectName: DEFAULT_LAUNCH_PROJECT_NAME,
      entityName,
    },
    skip: !runQueueID,
  });

  const allAgents = (
    launchAgentsQuery.data?.project?.launchAgents ?? []
  ).concat(launchAgentEntityQuery.data?.project?.launchAgents ?? []);

  if (allAgents?.length === 0) {
    return null;
  }

  const runningAgents = allAgents.filter(x =>
    x.runQueues.includes(runQueueID ?? '')
  );

  return runningAgents;
};

function getJobNameFromRunSpec(schedulerRunSpec: any): string | null {
  // Helper function get a job name from a run queue item runspec

  if (!schedulerRunSpec?.overrides?.entry_point) {
    return null;
  }

  const overrideList: string[] = schedulerRunSpec.overrides.entry_point;
  const idx = overrideList.findIndex(x => {
    return String(x).localeCompare('--job') === 0;
  });
  if (idx === -1) {
    return null;
  }
  return overrideList[idx + 1];
}

function useJobPathParams(
  entityName: string,
  jobName: string | null
): JobPathParams | null {
  const jobInfo = parseJobInput(jobName ?? undefined);
  // job name parsed from string, no version nor entitiy/project
  const simpleJobName: string | undefined = jobInfo?.jobName;

  const versionsQuery = useFetchJobVersionsQuery({
    variables: {
      entityName,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      projectName: jobInfo?.jobProject!,
      jobName: simpleJobName!,
    },
    skip: !jobName || !simpleJobName || !jobInfo?.jobProject,
  });

  if (
    !jobName ||
    !jobInfo?.jobProject ||
    versionsQuery.loading ||
    versionsQuery.error
  ) {
    return null;
  }

  const artifactId = versionsQuery.data?.project?.artifactCollection?.id;
  if (!artifactId) {
    return null;
  }

  const jobPathParams: JobPathParams = {
    entityName,
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    projectName: jobInfo?.jobProject!,
    jobId: artifactId,
    jobVersion: jobInfo?.jobVersion,
  };

  return jobPathParams;
}
